import { useState, useCallback } from 'react';
import api from '../../../../api/api';
import { fetchVendorListCached, getVendorListCached } from '../../../../utils/cache/vendorCache';
import { fetchManagerListCached, getManagerListCached } from '../../../../utils/cache/managerCache';

const buildVendorNameMap = (vendors = []) => {
    const map = new Map();
    vendors.forEach((vendor) => {
        const name = vendor.partner_name || vendor.vendor_name || vendor.company_name || '';
        [vendor.vendor_id, vendor.partner_id, vendor.company_id].forEach((id) => {
            if (id !== undefined && id !== null && !map.has(String(id))) {
                map.set(String(id), name);
            }
        });
    });
    return map;
};

const buildManagerNameMap = (managers = []) => {
    const map = new Map();
    managers.forEach((manager) => {
        const name = manager.manager_name || manager.name || '';
        [manager.manager_id, manager.id].forEach((id) => {
            if (id !== undefined && id !== null && !map.has(String(id))) {
                map.set(String(id), name);
            }
        });
    });
    return map;
};

export const useInvoiceManagementData = (cachedState) => {
    const [isLoading, setIsLoading] = useState(false);
    const [invoiceData, setInvoiceData] = useState(() => cachedState?.invoiceData ?? []);
    const [totalCount, setTotalCount] = useState(() => cachedState?.totalCount ?? 0);

    const fetchInvoiceData = useCallback(async (searchParams, setHasSearched) => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams();
            
            const startDateRaw = searchParams.startDate ?? searchParams.start_date;
            const endDateRaw = searchParams.endDate ?? searchParams.end_date;

            if (startDateRaw) {
                const startDate = typeof startDateRaw === 'object'
                    ? startDateRaw.format('YYYY-MM-DD')
                    : startDateRaw;
                params.append('start_date', startDate);
            }
            
            if (endDateRaw) {
                const endDate = typeof endDateRaw === 'object'
                    ? endDateRaw.format('YYYY-MM-DD')
                    : endDateRaw;
                params.append('end_date', endDate);
            }
            if (searchParams.invoice_no) {
                params.append('invoice_no', searchParams.invoice_no);
            }

            const response = await api.get(`/invoice/v1/invoice/list?${params.toString()}`);

            if (response.data) {
                let filteredData = response.data || [];
                
                if (searchParams.status && searchParams.status.trim() !== '') {
                    filteredData = filteredData.filter(invoice => 
                        invoice.status === searchParams.status
                    );
                }

                if (searchParams.status_account_receivable && searchParams.status_account_receivable.trim() !== '') {
                    filteredData = filteredData.filter(invoice => {
                        const receivableStatus = invoice.status_account_receivable;
                        
                        if (receivableStatus === searchParams.status_account_receivable) {
                            return true;
                        }
                        
                        if (searchParams.status_account_receivable === 'unpaid' && receivableStatus === 'issued') {
                            return true;
                        }
                        
                        return false;
                    });
                }

                let vendors = getVendorListCached() || [];
                let managers = getManagerListCached() || [];

                const shouldFetchCaches = (!vendors || vendors.length === 0) || (!managers || managers.length === 0);
                if (shouldFetchCaches) {
                    await Promise.all([
                        fetchVendorListCached(false),
                        fetchManagerListCached(false)
                    ]);
                    vendors = getVendorListCached() || [];
                    managers = getManagerListCached() || [];
                }

                const vendorNameMap = buildVendorNameMap(vendors);
                const managerNameMap = buildManagerNameMap(managers);

                const enrichedData = filteredData.map((invoice) => {
                    const customerId = invoice.customer_id ?? invoice.vendor_id;
                    const managerId = invoice.manager_id;

                    return {
                        ...invoice,
                        customer_name: invoice.customer_name || vendorNameMap.get(String(customerId)) || '',
                        manager_name: invoice.manager_name || managerNameMap.get(String(managerId)) || ''
                    };
                });

                // 고객사/담당자는 프론트에서 필터 적용 (Autocomplete 입력값 포함)
                let frontFiltered = enrichedData;
                if (searchParams.customer_name && String(searchParams.customer_name).trim() !== '') {
                    const q = String(searchParams.customer_name).trim().toLowerCase();
                    frontFiltered = frontFiltered.filter((row) =>
                        String(row.customer_name || '').toLowerCase().includes(q)
                    );
                }
                if (searchParams.manager && String(searchParams.manager).trim() !== '') {
                    const q = String(searchParams.manager).trim().toLowerCase();
                    frontFiltered = frontFiltered.filter((row) =>
                        String(row.manager_name || '').toLowerCase().includes(q)
                    );
                }

                setInvoiceData(frontFiltered);
                setTotalCount(frontFiltered.length);
            } else {
                setInvoiceData([]);
                setTotalCount(0);
            }
        } catch (error) {
            setInvoiceData([]);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
            setHasSearched(false);
        }
    }, []);

    const fetchInitialData = useCallback(async () => {
        const defaultParams = {
            startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            endDate: new Date().toISOString().split('T')[0]
        };
        
        await fetchInvoiceData(defaultParams, () => {});
    }, [fetchInvoiceData]);

    const updateInvoiceStatus = useCallback(async (invoiceId, status) => {
        try {
            const response = await api.patch(`/invoice/v1/invoice/item/${invoiceId}/status?status=${status}`);

            return response.status === 200;
        } catch (error) {
            return false;
        }
    }, []);

    return {
        isLoading,
        invoiceData,
        totalCount,
        fetchInvoiceData,
        fetchInitialData,
        updateInvoiceStatus
    };
}; 